<template>
    <div class="statement_card">
        <div class="card_inner">
            <div class="card_header is-flex is-justify-content-space-between is-align-items-center">
                <span class="period_label">{{ periodLabel }}</span>
                <div class="pills">
                    <span v-if="hasMom" class="pill pill--mom">{{ formatPct(headline.mom) }} MoM</span>
                    <span v-if="hasYoy" class="pill pill--yoy">{{ formatPct(headline.yoy) }} YoY</span>
                </div>
            </div>
            <p class="main_amount">{{ formatMoney(headline.amount) }}</p>

            <!-- Eficiencia total del mes (gasto total / ventas) — badge del
                headline, mismo lenguaje que el efficiency_badge del summary. -->
            <div v-if="headline.efficiency != null"
                class="efficiency_row headline_efficiency">
                <span class="eff_left is-flex is-align-items-center">
                    <ChartLine class="eff_icon" />
                    <span class="eff_label">Eficiencia</span>
                </span>
                <span class="eff_badge">{{ formatPctBadge(headline.efficiency) }}</span>
            </div>
        </div>

        <div class="statement">
            <!-- Clic en "Gastos" = todas las contrapartes del mes (deselecciona
                cualquier cuenta). Seleccionado por defecto (account null). -->
            <div class="statement_header is-flex is-align-items-center is-selectable"
                :class="{ 'is-selected': isAllSelected }"
                role="button"
                tabindex="0"
                @click="selectAll"
                @keydown.enter.prevent="selectAll"
                @keydown.space.prevent="selectAll">
                <PieChartIcon class="statement_icon" />
                <span class="statement_title">Gastos</span>
            </div>

            <ul class="statement_list">
                <template v-for="group in groups">
                    <!-- Clic en el grupo recorta el ranking a esa cuenta L2; el
                        chevron (clic aparte) sólo expande/colapsa sus hijos. -->
                    <li :key="group.level_2" class="group_header is-selectable"
                        :class="{ 'is-selected': isGroupSelected(group.level_2) }"
                        role="button"
                        tabindex="0"
                        @click="selectGroup(group.level_2)"
                        @keydown.enter.prevent="selectGroup(group.level_2)"
                        @keydown.space.prevent="selectGroup(group.level_2)">
                        <span class="group_label">{{ group.level_2 }}</span>
                        <span class="group_right is-flex is-align-items-center">
                            <span class="group_amount">{{ formatMoney(group.amount) }}</span>
                            <button type="button" class="chevron_btn"
                                :aria-expanded="String(isExpanded(group.level_2))"
                                aria-label="Expandir grupo"
                                @click.stop="toggle(group.level_2)">
                                <ArrowDown class="group_chevron"
                                    :class="{ 'rotate-icon': isExpanded(group.level_2) }" />
                            </button>
                        </span>
                    </li>

                    <template v-if="isExpanded(group.level_2)">
                        <li v-for="child in group.children"
                            :key="group.level_2 + '|' + child.account"
                            class="child_row is-selectable"
                            :class="{ 'is-selected': isChildSelected(child.account) }"
                            role="button"
                            tabindex="0"
                            @click="selectChild(child.account)"
                            @keydown.enter.prevent="selectChild(child.account)"
                            @keydown.space.prevent="selectChild(child.account)">
                            <span class="child_account">{{ child.account }}</span>
                            <span class="child_amount">{{ formatMoney(child.amount) }}</span>
                        </li>
                    </template>

                    <!-- Fila de eficiencia del grupo (gasto del grupo / ventas).
                        Sólo el badge con el % — no monto (a diferencia del margen
                        de Costos). -->
                    <li :key="group.level_2 + '|eff'" class="efficiency_row">
                        <span class="eff_left is-flex is-align-items-center">
                            <ChartLine class="eff_icon" />
                            <span class="eff_label">{{ group.efficiency.label }}</span>
                        </span>
                        <span v-if="group.efficiency.pct != null" class="eff_badge">
                            {{ formatPctBadge(group.efficiency.pct) }}
                        </span>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
    name: "ExpenseStatementCard",
    components: { PieChartIcon, ChartLine, ArrowDown },
    props: {
        periodLabel: { type: String, default: "" },
        // { amount, mom, yoy, efficiency } — gasto total del mes + comparables +
        // eficiencia total (gasto/ventas).
        headline: {
            type: Object,
            required: true,
            validator: (v) => v && typeof v.amount === "number",
        },
        // { sales_total, total_costs, groups: [{ level_2, amount, children,
        // efficiency: { label, pct } }] }.
        statement: {
            type: Object,
            default: () => ({ groups: [] }),
        },
        // Cuenta seleccionada para recortar el ranking de contrapartes (la
        // gobierna el padre vía el store). null = "Gastos" (todas).
        selectedAccount: {
            type: String,
            default: null,
        },
        // Nivel de `selectedAccount`: "2" (grupo) o "3" (cuenta). null con "todas".
        selectedLevel: {
            type: String,
            default: null,
        },
    },
    data() {
        // level_2 de los grupos expandidos. Todos abiertos por defecto — el
        // detalle del estado es la lectura principal de la card.
        return { expandedGroups: [] };
    },
    computed: {
        groups() {
            return this.statement?.groups ?? [];
        },
        hasMom() {
            return this.headline.mom != null;
        },
        hasYoy() {
            return this.headline.yoy != null;
        },
        // "Gastos" (todas las contrapartes) está activo cuando no hay cuenta.
        isAllSelected() {
            return this.selectedAccount == null;
        },
    },
    watch: {
        // Cada refetch reabre todos los grupos — estado fresco.
        statement: {
            immediate: true,
            handler() {
                this.expandedGroups = this.groups.map((g) => g.level_2);
            },
        },
    },
    methods: {
        formatMoney(v) {
            return parseToMoney(v);
        },
        // Mismo formato que CostStatementCard: el color del pill conduce la
        // jerarquía MoM/YoY; el "-" se conserva para negativos.
        formatPct(pct) {
            return formatVariation(pct, { dash: "" });
        },
        // Badge de eficiencia: porcentaje entero, coherente con los shares
        // enteros del ranking del mismo modal.
        formatPctBadge(pct) {
            return `${Math.round(pct * 100)}%`;
        },
        isExpanded(level2) {
            return this.expandedGroups.includes(level2);
        },
        toggle(level2) {
            const idx = this.expandedGroups.indexOf(level2);
            if (idx > -1) this.expandedGroups.splice(idx, 1);
            else this.expandedGroups.push(level2);
        },
        // --- Selección de cuenta para recortar el ranking de contrapartes ---
        isGroupSelected(level2) {
            return this.selectedLevel === "2" && this.selectedAccount === level2;
        },
        isChildSelected(account) {
            return this.selectedLevel === "3" && this.selectedAccount === account;
        },
        selectAll() {
            this.$emit("select-account", { account: null, level: null });
        },
        selectGroup(level2) {
            this.$emit("select-account", { account: level2, level: "2" });
        },
        selectChild(account) {
            this.$emit("select-account", { account, level: "3" });
        },
    },
};
</script>

<style scoped lang="scss">
.statement_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: var(--report-shadow-card);
    padding: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_inner {
    padding: 20px 20px 10px 20px;
    background: var(--report-surface);
    border-radius: 12px;
    border: 1px solid var(--report-border);
    flex-shrink: 0;
}

.card_header {
    margin-bottom: 12px;
}

.period_label {
    color: var(--report-text-muted);
    font-size: 14px;
    font-weight: 500;
}

.pills {
    display: flex;
    gap: 6px;
}

.pill {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid;
}

.pill--mom {
    background: var(--report-accent-bg);
    color: var(--report-accent-text);
    border-color: var(--report-accent-border);
}

.pill--yoy {
    background: transparent;
    color: var(--report-accent-text);
    border-color: var(--report-accent-border);
}

.main_amount {
    font-size: 36px;
    font-weight: 600;
    color: var(--report-text);
    letter-spacing: -0.5px;
    margin: 0;
    line-height: 1.1;
}

// Fila de eficiencia del headline: separada del monto, dentro del inner gris.
.headline_efficiency {
    margin-top: 12px;
}

.statement {
    padding: 16px 4px 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.statement_header {
    margin-bottom: 8px;
    padding: 6px 4px;
    color: var(--report-text);
    flex-shrink: 0;

    .statement_icon {
        width: 16px;
        height: 16px;
        margin-right: 8px;
        flex-shrink: 0;
        color: var(--report-text);
    }

    .statement_title {
        font-size: 14px;
        font-weight: 600;
    }
}

.statement_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

// Filas seleccionables del estado (Gastos / grupos L2 / cuentas L3): el clic
// recorta el ranking de contrapartes a esa cuenta. La fila activa se resalta
// con el accent del tab + una barra a la izquierda; el resto queda neutro, así
// la lista diferencia claramente lo seleccionado de lo no seleccionado.
.is-selectable {
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.12s ease;

    &:hover:not(.is-selected) {
        background: var(--report-surface);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
    }
}

.is-selected {
    background: var(--report-accent-bg);
    box-shadow: inset 3px 0 0 var(--report-accent-border);
}

// El chevron expande/colapsa el grupo — clic independiente del de selección.
.chevron_btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;

    &:hover { background: var(--report-divider-soft); }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 1px;
    }
}

.group_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 2px;
    border-top: 1px solid var(--report-divider-soft);
    cursor: pointer;
    font-size: 14px;

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
        border-radius: 6px;
    }

    .group_label {
        color: var(--report-text);
        font-weight: 600;
    }

    .group_right {
        gap: 8px;
    }

    .group_amount {
        color: var(--report-text);
        font-weight: 600;
    }

    .group_chevron {
        width: 13px;
        height: 13px;
        color: var(--report-text-muted-soft);
        transition: transform 0.2s ease;
    }

    .rotate-icon {
        transform: rotate(180deg);
    }
}

.child_row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 2px 7px 20px;
    font-size: 13px;

    .child_account {
        color: var(--report-text-muted);
    }

    .child_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }
}

// Fila de eficiencia — la conclusión de cada grupo (y del headline). Color
// accent del tab (ámbar en Gastos) + badge con el %. Mismo lenguaje que el
// margin_row de Costos pero sin monto (la eficiencia es sólo un %).
.efficiency_row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px;
    font-size: 13px;

    .eff_left {
        gap: 6px;
    }

    .eff_icon {
        width: 14px;
        height: 14px;
        color: var(--report-accent-text);
    }

    .eff_label {
        color: var(--report-accent-text);
        font-weight: 600;
    }

    .eff_badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 999px;
        background: var(--report-accent-bg);
        color: var(--report-accent-text);
        border: 1px solid var(--report-accent-border);
        font-size: 12px;
        font-weight: 700;
    }
}
</style>
